import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getLineItemSelector,
  getDiscountSelector,
  getCurrencyCodeSelector,
  getSubscriptionCycleSelector,
  getSubscriptionPensionSelector,
} from '../../../core/selectors';
import { getVariantProductInfoSelector } from '@modules/products/core/selectors';
import { getDisabledRecipes } from '../../../utils/getDisabledRecipes';
import { findMatchingVariant, hasMatchingVariant } from '../utils';
import { IPet, Recipe, Pension, Cycle } from '../../../core/types';

interface UsePetHealthPreviewProps {
  pet: IPet;
  selectedAllergies: string[];
  selectedHealthIssues: string[];
  recalculatedProduct: any;
  currentRecipes: string[];
  onSave: (variantId?: number) => void;
}

export const usePetHealthPreview = ({
  pet,
  selectedAllergies,
  selectedHealthIssues,
  recalculatedProduct,
  currentRecipes,
  onSave,
}: UsePetHealthPreviewProps) => {
  const lineItem = useSelector(getLineItemSelector);
  const discount = useSelector(getDiscountSelector);
  const currencyCode = useSelector(getCurrencyCodeSelector);
  const variantProductInfo = useSelector(getVariantProductInfoSelector);
  const currentCycle = useSelector(getSubscriptionCycleSelector) as Cycle;
  const currentPension = useSelector(getSubscriptionPensionSelector) as Pension;

  const allergyDisabledRecipes = useMemo(() => {
    const tempPet = { ...pet, allergies: selectedAllergies, health_issues: selectedHealthIssues };
    return getDisabledRecipes(tempPet as any);
  }, [pet, selectedAllergies, selectedHealthIssues]);

  const [selectedRecipes, setSelectedRecipes] = useState<Recipe[]>(() => {
    const validRecipes = currentRecipes.filter((r) => !allergyDisabledRecipes.includes(r as any));
    return (validRecipes.length > 0 ? validRecipes : currentRecipes) as Recipe[];
  });

  const disabledRecipes = useMemo(() => {
    const recalculatedDisabled = recalculatedProduct?.restrictions?.disabledRecipes || [];
    return Array.from(new Set([...allergyDisabledRecipes, ...recalculatedDisabled]));
  }, [allergyDisabledRecipes, recalculatedProduct]);

  const variants = useMemo(() => recalculatedProduct?.product?.variants, [recalculatedProduct]);

  useEffect(() => {
    if (!variants) return;

    const validCurrentRecipes = currentRecipes.filter((r) => !disabledRecipes.includes(r as any));
    const matchesCurrent =
      validCurrentRecipes.length > 0 &&
      hasMatchingVariant(variants, {
        recipes: validCurrentRecipes as Recipe[],
        pension: currentPension,
        cycle: currentCycle,
      });

    if (matchesCurrent) {
      setSelectedRecipes(validCurrentRecipes as Recipe[]);
      return;
    }

    if (recalculatedProduct?.recommendedVariant) {
      const variant = variants.find(
        (v: any) => String(v.id) === String(recalculatedProduct.recommendedVariant.id),
      );
      if (variant && variant.selectedOptions?.recipes) {
        setSelectedRecipes(variant.selectedOptions.recipes);
      }
    } else if (recalculatedProduct?.recommendedRecipes) {
      setSelectedRecipes(recalculatedProduct.recommendedRecipes);
    }
  }, [recalculatedProduct, variants, currentRecipes, disabledRecipes]);

  const oldPrice = lineItem?.discountedPrice || 0;

  const currentVariant = useMemo(() => {
    if (!variants || selectedRecipes.length === 0) return null;
    // Try exact match first (recipes + cycle + pension)
    const exact = findMatchingVariant(variants, {
      recipes: selectedRecipes,
      pension: currentPension,
      cycle: currentCycle,
    });
    if (exact) return exact;
    // Fallback: match by recipes only (e.g. when no exact variant exists)
    return findMatchingVariant(variants, { recipes: selectedRecipes });
  }, [variants, selectedRecipes, currentPension, currentCycle]);

  const initialSachets = useMemo(() => {
    return variantProductInfo?.sachets || null;
  }, [variantProductInfo]);

  const sachets = currentVariant?.metafields?.product_info?.sachets || initialSachets;

  const newBasePrice = currentVariant ? parseFloat(String(currentVariant.price || '0')) : oldPrice;

  const discountPercentage = useMemo<number | null>(() => {
    if (lineItem && lineItem.basePrice > 0 && lineItem.discountedPrice < lineItem.basePrice) {
      return ((lineItem.basePrice - lineItem.discountedPrice) / lineItem.basePrice) * 100;
    }
    if (
      discount?.value &&
      typeof discount.value === 'object' &&
      'percentage' in discount.value &&
      typeof discount.value.percentage === 'number'
    ) {
      return discount.value.percentage;
    }
    return null;
  }, [lineItem, discount]);

  const newPrice =
    discountPercentage && discountPercentage > 0
      ? newBasePrice - (newBasePrice * discountPercentage) / 100
      : newBasePrice;

  const handleRecipeToggle = (recipe: Recipe) => {
    const isSelected = selectedRecipes.includes(recipe);
    const isDisabled = disabledRecipes.includes(recipe);

    if (isDisabled) return;

    let newRecipes: Recipe[];
    if (isSelected) {
      if (selectedRecipes.length <= 1) return;
      newRecipes = selectedRecipes.filter((r) => r !== recipe);
    } else {
      newRecipes = [...selectedRecipes, recipe];
    }

    if (variants && !hasMatchingVariant(variants, { recipes: newRecipes })) {
      const fallback = findMatchingVariant(variants, { recipes: newRecipes });
      if (!fallback) {
        // No matching variant
      }
    }

    setSelectedRecipes(newRecipes);
  };

  const handleSaveClick = () => {
    const variantId = currentVariant ? parseInt(currentVariant.id) : undefined;
    onSave(variantId);
  };

  return {
    selectedRecipes,
    disabledRecipes,
    sachets,
    oldPrice,
    newPrice,
    currencyCode,
    discount,
    currentVariant,
    handleRecipeToggle,
    handleSaveClick,
  };
};
